import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActionButtonVisibilityService } from '@netfoundry-ui/feature/action-button';
import { OidcIssuerService } from '@netfoundry-ui/shared/services';
import { RecordDetailAction, RecordDetailSection } from '@netfoundry-ui/ui/detail-drawer';
import { forgetFixedColumnWidths, oidcListTabs, ROW_MENU_ICONS } from '@netfoundry-ui/ui/table';
import _ from 'lodash';
import { skip } from 'rxjs/operators';
import { DataTableFilterService, FilterObj, ListColumn, ListSortDir, ListTableService } from 'ziti-console-lib';
import {
    filterIssuers,
    ISSUER_FILTER,
    JWKS_FILTER,
    OidcIssuerRow,
    OidcIssuerSortKey,
    sortIssuers,
} from './oidc-issuers-list.helpers';
import { OidcIssuersComponent } from './oidc-issuers.component';

const TABLE_ID = 'oidc-issuers-list';
const DEFAULT_PAGE_SIZE = 30;

function formatDate(value: string | undefined): string {
    const date = value ? new Date(value) : undefined;
    return date && !isNaN(date.getTime()) ? date.toLocaleString() : value || '';
}

@Component({
    selector: 'app-oidc-issuers-list',
    templateUrl: './oidc-issuers-list.component.html',
    styleUrls: ['./oidc-issuers-list.component.scss'],
    providers: [OidcIssuerService],
    standalone: false,
})
export class OidcIssuersListComponent extends OidcIssuersComponent implements OnInit, OnDestroy {
    readonly tableId = TABLE_ID;
    readonly searchFilter = ISSUER_FILTER;
    readonly initialPageSize = DEFAULT_PAGE_SIZE;
    readonly tableOptions = { pager: 'pages' as const };
    readonly menuItems = [
        { label: 'Edit', action: 'update', icon: ROW_MENU_ICONS.edit },
        { label: 'Delete', action: 'delete', icon: ROW_MENU_ICONS.delete },
    ];

    readonly tabs = oidcListTabs();

    columns: ListColumn<OidcIssuerRow>[] = [];
    selectedId = '';
    selected: OidcIssuerRow | undefined;
    displayed: OidcIssuerRow[] = [];
    filteredCount = 0;
    listPage = 1;
    listFilterApplied = false;

    private readonly tableFilters = inject(DataTableFilterService);
    readonly createVisibility = inject(ActionButtonVisibilityService);
    private listFilters: Record<string, string> = { [ISSUER_FILTER]: '', [JWKS_FILTER]: '' };
    private sortKey: OidcIssuerSortKey = 'issuer';
    private sortDir: 'asc' | 'desc' = 'asc';

    get canCreate(): boolean {
        return this.authorizationService.canCreateOIDCIssuer();
    }

    get accessDenied(): boolean {
        return !this.authorizationService.canListOIDCIssuer();
    }

    // Same rule the old plus/minus button hid itself by.
    get createBlocked(): boolean {
        return !this.canCreate || this.createVisibility.isCancelledAccount;
    }

    get hasSelection(): boolean {
        return this.selectedIds.length > 0;
    }

    get selectedIndex(): number {
        return this.displayed.findIndex((row) => row.id === this.selectedId);
    }

    get hasPrev(): boolean {
        return this.listPage > 1;
    }

    get hasNext(): boolean {
        return this.listPage * this.tableFilters.pageSize < this.filteredCount;
    }

    get startCount(): number {
        return this.displayed.length ? (this.listPage - 1) * this.tableFilters.pageSize + 1 : 0;
    }

    get endCount(): number {
        return this.displayed.length ? (this.listPage - 1) * this.tableFilters.pageSize + this.displayed.length : 0;
    }

    override ngOnInit(): void {
        this.columns = this.buildColumns();
        forgetFixedColumnWidths(TABLE_ID, this.columns);
        this.tableFilters.pageSize = ListTableService.readPersistedPageSize(TABLE_ID, DEFAULT_PAGE_SIZE);
        this.tableFilters.clearFilters();
        this.subscription.add(
            this.tableFilters.filtersChanged.subscribe((filters) => {
                this.applyFilters(filters);
                this.restart();
            })
        );
        this.subscription.add(
            this.tableFilters.pageChanged.pipe(skip(1)).subscribe((page) => {
                const next = Math.max(1, Number(page) || 1);
                // Refresh and auto-refresh ask for the page on screen again, so that is a reload.
                if (next === this.listPage) {
                    this.refresh();
                    return;
                }
                this.listPage = next;
                this.applyView();
            })
        );
        this.subscription.add(this.tableFilters.pageSizeChanged.pipe(skip(1)).subscribe(() => this.restart()));
        this.isLoading = true;
        this.handlePermissionCheck();
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.tableFilters.clearFilters();
    }

    protected override onIssuersLoaded(): void {
        (this.oidcIssuers as OidcIssuerRow[]).forEach((row) => {
            row.hideSelect = !row.actionList?.includes('delete');
        });
        this.applyView();
    }

    onAction(event: { action: string; item?: OidcIssuerRow }): void {
        const row = event.item;
        if (event.action === 'create') {
            this.create();
        } else if (event.action === 'update' && row) {
            this.open(row);
        } else if (event.action === 'delete' && row) {
            this.deleteSingleItem(row);
        }
    }

    onHeaderAction(action: string): void {
        if (action === 'add') {
            this.create();
        } else if (action === 'delete' && this.hasSelection) {
            this.openConfirm(undefined);
        }
    }

    select(row: OidcIssuerRow): void {
        this.selectedId = this.selectedId === row.id ? '' : row.id;
        this.markSelected();
    }

    closeDetail(): void {
        this.selectedId = '';
        this.markSelected();
    }

    navigateDetail(step: -1 | 1): void {
        const next = this.displayed[this.selectedIndex + step];
        if (next) {
            this.selectedId = next.id;
            this.markSelected();
        }
    }

    detailSections(row: OidcIssuerRow): RecordDetailSection[] {
        return [
            {
                title: 'Issuer',
                fields: [
                    { label: 'Issuer', value: row.issuer, mono: true },
                    { label: 'JWKS URI', value: row.jwksUri, mono: true },
                    { label: 'Status', value: row.active ? 'Active' : 'Inactive' },
                ],
            },
            {
                title: 'Record',
                fields: [
                    { label: 'Id', value: row.id, mono: true },
                    { label: 'Created', value: formatDate(row.createdAt) },
                    { label: 'Created By', value: row.createdBy, mono: true },
                ],
            },
        ];
    }

    detailActions(row: OidcIssuerRow): RecordDetailAction[] {
        const actions: RecordDetailAction[] = [{ label: 'Edit', action: 'update' }];
        if (row.actionList?.includes('delete')) {
            actions.push({ label: 'Delete', action: 'delete', danger: true });
        }
        return actions;
    }

    // Mirrors the old table's toggle rules: with delete protection on, only a single selection can be deleted.
    onSelectionChanged(selected: OidcIssuerRow[]): void {
        const ids = new Set((selected || []).map((row) => row.id));
        this.displayed.forEach((row) => (row.selected = ids.has(row.id)));
        const chosen = (this.oidcIssuers as OidcIssuerRow[]).filter((row) => row.selected);
        this.selectedIds = chosen.map((row) => row.id);
        this.enableDeleteProtection = this.featureService.enableDeleteProtection;
        this.isDeleteAction = chosen.length > 0 && (!this.enableDeleteProtection || chosen.length === 1);
        this.allToggled = false;
    }

    private applyFilters(filters: FilterObj[]): void {
        const next: Record<string, string> = { [ISSUER_FILTER]: '', [JWKS_FILTER]: '' };
        filters.forEach((item) => {
            if (item.columnId in next) {
                next[item.columnId] = String(item.value ?? '').trim();
            }
        });
        this.listFilters = next;
        this.listFilterApplied = Object.values(next).some((value) => !!value);
    }

    private restart(): void {
        this.listPage = 1;
        this.tableFilters.currentPage = 1;
        this.applyView();
    }

    private applyView(): void {
        const sorted = sortIssuers(
            filterIssuers(this.oidcIssuers as OidcIssuerRow[], this.listFilters),
            this.sortKey,
            this.sortDir
        );
        const pageSize = this.tableFilters.pageSize;
        const lastPage = Math.max(1, Math.ceil(sorted.length / pageSize));
        if (this.listPage > lastPage) {
            this.listPage = lastPage;
            this.tableFilters.currentPage = lastPage;
        }
        this.filteredCount = sorted.length;
        const start = (this.listPage - 1) * pageSize;
        this.displayed = sorted.slice(start, start + pageSize);
        this.markSelected();
    }

    private markSelected(): void {
        this.selected = this.selectedId ? this.displayed.find((row) => row.id === this.selectedId) : undefined;
        if (!this.selected) {
            this.selectedId = '';
        }
        this.displayed.forEach((row) => (row['open'] = row.id === this.selectedId));
    }

    private buildColumns(): ListColumn<OidcIssuerRow>[] {
        const open = (ctx: { row: OidcIssuerRow }) => this.select(ctx.row);
        const onSort = (key: string, dir: ListSortDir) => {
            this.sortKey = key as OidcIssuerSortKey;
            this.sortDir = dir === 'desc' ? 'desc' : 'asc';
            this.restart();
        };
        return [
            {
                key: ISSUER_FILTER,
                label: 'Issuer',
                minWidth: 250,
                hideable: false,
                sortable: true,
                sortDir: 'asc',
                filterType: 'TEXTINPUT',
                onSort,
                valueFormatter: (shim) => _.escape(shim.data.issuer || ''),
                onCellClick: open,
            },
            {
                key: JWKS_FILTER,
                label: 'JWKS URI',
                minWidth: 250,
                sortable: true,
                filterType: 'TEXTINPUT',
                onSort,
                valueFormatter: (shim) => _.escape(shim.data.jwksUri || ''),
                onCellClick: open,
            },
            {
                key: 'active',
                label: 'Active',
                width: 130,
                sortable: true,
                onSort,
                cellHtml: (shim) =>
                    shim.data.active
                        ? '<span class="lt-status lt-status-online">Active</span>'
                        : '<span class="lt-status lt-status-offline">Inactive</span>',
                onCellClick: open,
            },
        ];
    }
}
